import { useState, useEffect, useRef, useCallback } from "react";

const API = "";
const REFRESH_MS = 30000;
const PRICE_REFRESH_MS = 5000;

// ─── Theme ────────────────────────────────────────────────────────────────────
const T = {
  bg:        "#0a0c10",
  surface:   "#111318",
  border:    "#1e2228",
  accent:    "#00d4ff",
  accentDim: "#00d4ff22",
  green:     "#00e676",
  red:       "#ff3d71",
  yellow:    "#ffd600",
  blue:      "#448aff",
  purple:    "#7c4dff",
  text:      "#e8eaf0",
  textDim:   "#6b7280",
  textMuted: "#374151",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${T.bg}; color: ${T.text}; font-family: 'Inter', sans-serif; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: ${T.bg}; }
  ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 2px; }
  .mono { font-family: 'JetBrains Mono', monospace; }
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
  @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  @keyframes glow { 0%,100% { box-shadow: 0 0 8px ${T.accent}44; } 50% { box-shadow: 0 0 20px ${T.accent}88; } }
  .live-dot { width:8px; height:8px; border-radius:50%; background:${T.green}; animation:pulse 2s infinite; display:inline-block; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt  = (n, d=2) => n == null ? "—" : Number(n).toFixed(d);
const fmtK = (n) => n == null ? "—" : n >= 1000 ? `$${(n/1000).toFixed(1)}k` : `$${Number(n).toFixed(2)}`;
const pct  = (n) => n == null ? "—" : `${n > 0 ? "+" : ""}${Number(n).toFixed(2)}%`;
const clr  = (n) => n > 0 ? T.green : n < 0 ? T.red : T.textDim;

// ─── Chart: Price History + Levels + Projection ───────────────────────────────
function PositionChart({ pos, projection }) {
  const svgRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [pos.symbol]);

  async function fetchHistory() {
    setLoading(true);
    try {
      // Fetch 30 days of daily candles from Binance
      const pair = pos.symbol + "USDT";
      const resp = await fetch(
        `https://api.binance.us/api/v3/klines?symbol=${pair}&interval=1d&limit=30`
      );
      if (resp.ok) {
        const data = await resp.json();
        const prices = data.map(d => ({
          t: new Date(d[0]),
          o: parseFloat(d[1]),
          h: parseFloat(d[2]),
          l: parseFloat(d[3]),
          c: parseFloat(d[4]),
        }));
        setHistory(prices);
      }
    } catch (e) {
      console.error("History fetch error:", e);
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
                    height:280, color:T.textDim, fontSize:13 }}>
        Loading price history...
      </div>
    );
  }

  if (!history.length) return null;

  // ── Build chart data ───────────────────────────────────────────────────────
  const entry   = parseFloat(pos.entry_price);
  const current = parseFloat(pos.current_price || pos.entry_price);
  const stop    = parseFloat(pos.stop_loss);
  const tp1     = parseFloat(pos.take_profit_1 || 0);
  const tp2     = parseFloat(pos.take_profit || 0);

  // 7-day projection points
  const projCenter = projection?.center;
  const projLow    = projection?.low;
  const projHigh   = projection?.high;

  const now     = new Date();
  const day7    = new Date(now.getTime() + 7 * 86400000);

  // All price points for scale
  const allPrices = [
    ...history.map(d => d.l),
    ...history.map(d => d.h),
    stop, entry, tp2,
    ...(projLow  ? [projLow]    : []),
    ...(projHigh ? [projHigh]   : []),
    current,
  ].filter(Boolean);

  const minP = Math.min(...allPrices) * 0.995;
  const maxP = Math.max(...allPrices) * 1.005;
  const W = 700, H = 260, PAD_L = 70, PAD_R = 80, PAD_T = 15, PAD_B = 30;
  const CW = W - PAD_L - PAD_R;
  const CH = H - PAD_T - PAD_B;

  // Time scale: history start → day7
  const tMin = history[0].t.getTime();
  const tMax = day7.getTime();
  const tRange = tMax - tMin;
  const tNow   = now.getTime();

  const tx = t  => PAD_L + ((t - tMin) / tRange) * CW;
  const py = p  => PAD_T + CH - ((p - minP) / (maxP - minP)) * CH;
  const fmtDate = d => d.toLocaleDateString("en-US", { month:"short", day:"numeric" });

  // Build close price path
  const closePath = history.map((d, i) => {
    const x = tx(d.t.getTime());
    const y = py(d.c);
    return `${i === 0 ? "M" : "L"} ${x} ${y}`;
  }).join(" ");

  const nowX = tx(tNow);

  // Projection path
  let projPath = "";
  let projArea = "";
  if (projCenter && projLow && projHigh) {
    const x7  = tx(day7.getTime());
    const yC  = py(projCenter);
    const yL  = py(projLow);
    const yH  = py(projHigh);
    const yCu = py(current);

    projPath = `M ${nowX} ${yCu} L ${x7} ${yC}`;
    projArea = `M ${nowX} ${yCu} L ${x7} ${yH} L ${x7} ${yL} Z`;
  }

  // Y axis labels
  const yTicks = 4;
  const yLabels = Array.from({ length: yTicks + 1 }, (_, i) => {
    const price = minP + (i / yTicks) * (maxP - minP);
    return { price, y: py(price) };
  });

  // X axis labels
  const xLabels = [
    history[0].t,
    history[Math.floor(history.length / 2)].t,
    now,
    day7,
  ];

  const pnlPct = ((current - entry) / entry * 100);

  return (
    <div style={{ animation: "fadeIn 0.4s ease" }}>
      {/* Mini stats row */}
      <div style={{ display:"flex", gap:24, marginBottom:16, flexWrap:"wrap" }}>
        {[
          { label:"Entry",   val:`$${fmtK(entry).replace("$","")}`,    color:T.blue   },
          { label:"Current", val:`$${fmtK(current).replace("$","")}`,  color:clr(pnlPct) },
          { label:"P&L",     val:pct(pnlPct),                           color:clr(pnlPct) },
          { label:"Stop",    val:`$${fmtK(stop).replace("$","")}`,     color:T.red    },
          { label:"TP1",     val:`$${fmtK(tp1).replace("$","")}`,      color:T.yellow },
          { label:"TP2",     val:`$${fmtK(tp2).replace("$","")}`,      color:T.green  },
        ].map(s => (
          <div key={s.label}>
            <div style={{ fontSize:10, color:T.textDim, textTransform:"uppercase",
                          letterSpacing:"0.08em", marginBottom:2 }}>{s.label}</div>
            <div className="mono" style={{ fontSize:13, color:s.color, fontWeight:500 }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* SVG Chart */}
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" style={{ overflow:"visible" }}>
        <defs>
          <linearGradient id={`grad-${pos.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.accent} stopOpacity="0.15" />
            <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`proj-grad-${pos.id}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={T.purple} stopOpacity="0.3" />
            <stop offset="100%" stopColor={T.purple} stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {yLabels.map((l, i) => (
          <g key={i}>
            <line x1={PAD_L} y1={l.y} x2={W - PAD_R} y2={l.y}
                  stroke={T.border} strokeWidth="1" strokeDasharray="3,6" />
            <text x={PAD_L - 6} y={l.y + 4} textAnchor="end"
                  fill={T.textDim} fontSize="10" fontFamily="JetBrains Mono">
              {l.price >= 1000 ? `${(l.price/1000).toFixed(1)}k` : l.price.toFixed(2)}
            </text>
          </g>
        ))}

        {/* X axis labels */}
        {xLabels.map((d, i) => {
          const x = tx(d.getTime());
          const isNow = i === 2;
          return (
            <text key={i} x={x} y={H - 4} textAnchor="middle"
                  fill={isNow ? T.accent : T.textDim} fontSize="9"
                  fontFamily="JetBrains Mono" fontWeight={isNow ? "600" : "400"}>
              {isNow ? "NOW" : fmtDate(d)}
            </text>
          );
        })}

        {/* Area fill under history */}
        <path d={`${closePath} L ${tx(history[history.length-1].t.getTime())} ${PAD_T+CH} L ${tx(history[0].t.getTime())} ${PAD_T+CH} Z`}
              fill={`url(#grad-${pos.id})`} />

        {/* History line */}
        <path d={closePath} fill="none" stroke={T.accent}
              strokeWidth="1.5" strokeLinejoin="round" />

        {/* "NOW" vertical line */}
        <line x1={nowX} y1={PAD_T} x2={nowX} y2={PAD_T + CH}
              stroke={T.accent} strokeWidth="1" strokeDasharray="4,4" opacity="0.6" />

        {/* Projection cone */}
        {projArea && (
          <path d={projArea} fill={`url(#proj-grad-${pos.id})`} />
        )}

        {/* Projection center line */}
        {projPath && (
          <path d={projPath} fill="none" stroke={T.purple}
                strokeWidth="1.5" strokeDasharray="6,4" />
        )}

        {/* Horizontal level lines */}
        {[
          { price:entry, color:T.blue,   label:"Entry", dash:"" },
          { price:stop,  color:T.red,    label:"Stop",  dash:"4,3" },
          { price:tp1,   color:T.yellow, label:"TP1",   dash:"4,3" },
          { price:tp2,   color:T.green,  label:"TP2",   dash:"4,3" },
        ].filter(l => l.price > 0).map(l => {
          const y = py(l.price);
          return (
            <g key={l.label}>
              <line x1={PAD_L} y1={y} x2={nowX} y2={y}
                    stroke={l.color} strokeWidth="1"
                    strokeDasharray={l.dash} opacity="0.6" />
              <text x={W - PAD_R + 4} y={y + 4}
                    fill={l.color} fontSize="9" fontFamily="JetBrains Mono">
                {l.label}
              </text>
            </g>
          );
        })}

        {/* Entry dot */}
        <circle cx={tx(tMin + (tNow - tMin) * 0.05)} cy={py(entry)}
                r="4" fill={T.blue} stroke={T.bg} strokeWidth="2" />

        {/* Current price dot */}
        <circle cx={nowX} cy={py(current)}
                r="5" fill={clr(pnlPct)} stroke={T.bg} strokeWidth="2"
                style={{ animation:"glow 2s infinite" }} />

        {/* Projection center dot */}
        {projCenter && (
          <circle cx={tx(day7.getTime())} cy={py(projCenter)}
                  r="4" fill={T.purple} stroke={T.bg} strokeWidth="2" />
        )}
      </svg>

      {/* Projection rationale */}
      {projection && (
        <div style={{ marginTop:12, padding:"10px 14px",
                      background:`${T.purple}15`, borderRadius:8,
                      border:`1px solid ${T.purple}30` }}>
          <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
            <span style={{ fontSize:10, color:T.purple, textTransform:"uppercase",
                           letterSpacing:"0.1em", fontWeight:600 }}>AI Projection</span>
            <span className="mono" style={{ fontSize:11, color:T.text }}>
              ${fmt(projCenter, 4)} ({projection.bias})
            </span>
            <span style={{ fontSize:10, color:T.textDim }}>
              Range: ${fmt(projLow, 4)} – ${fmt(projHigh, 4)}
            </span>
            <span style={{ fontSize:10, color:T.textDim }}>
              Confidence: {projection.confidence}%
            </span>
          </div>
          <div style={{ marginTop:6, fontSize:11, color:T.textDim, lineHeight:1.5 }}>
            {projection.rationale}
          </div>
          <div style={{ marginTop:4, fontSize:9, color:T.textMuted }}>
            Generated: {projection.generated_at
              ? new Date(projection.generated_at).toLocaleString()
              : "this scan"}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Charts Tab ───────────────────────────────────────────────────────────────
function ChartsTab({ positions, projections }) {
  const [activeSymbol, setActiveSymbol] = useState(null);

  const openPos = positions.filter(p => p.status === "OPEN" || !p.status);

  useEffect(() => {
    if (openPos.length > 0 && !activeSymbol) {
      setActiveSymbol(openPos[0].symbol + "_" + openPos[0].profile);
    }
  }, [openPos]);

  if (!openPos.length) {
    return (
      <div style={{ textAlign:"center", padding:60, color:T.textDim }}>
        <div style={{ fontSize:32, marginBottom:12 }}>📊</div>
        <div style={{ fontSize:14 }}>No open positions to chart</div>
        <div style={{ fontSize:12, marginTop:6 }}>Charts appear when positions are open</div>
      </div>
    );
  }

  const activePos = openPos.find(p => p.symbol + "_" + p.profile === activeSymbol);

  return (
    <div>
      {/* Coin tabs */}
      <div style={{ display:"flex", gap:8, marginBottom:24, flexWrap:"wrap" }}>
        {openPos.map(p => {
          const key    = p.symbol + "_" + p.profile;
          const active = key === activeSymbol;
          const pnl    = parseFloat(p.pnl_pct || 0);
          return (
            <button key={key} onClick={() => setActiveSymbol(key)}
              style={{
                padding:"8px 16px", borderRadius:8, cursor:"pointer",
                border:`1px solid ${active ? T.accent : T.border}`,
                background: active ? T.accentDim : T.surface,
                color: active ? T.accent : T.text,
                fontSize:13, fontWeight:500, transition:"all 0.2s",
                fontFamily:"inherit",
              }}>
              {p.symbol}
              <span style={{ fontSize:10, marginLeft:6,
                             color:clr(pnl), fontFamily:"JetBrains Mono" }}>
                {pct(pnl)}
              </span>
              <span style={{ fontSize:9, marginLeft:4, color:T.textDim,
                             textTransform:"uppercase" }}>
                {p.profile}
              </span>
            </button>
          );
        })}
      </div>

      {/* Active chart */}
      {activePos && (
        <div style={{ background:T.surface, borderRadius:12,
                      border:`1px solid ${T.border}`, padding:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between",
                        alignItems:"center", marginBottom:16 }}>
            <div>
              <span style={{ fontSize:18, fontWeight:600, color:T.text }}>
                {activePos.symbol}
              </span>
              <span style={{ fontSize:12, color:T.textDim, marginLeft:8,
                             textTransform:"uppercase", letterSpacing:"0.08em" }}>
                {activePos.profile} profile
              </span>
            </div>
            <div className="mono" style={{
              fontSize:13, color:T.textDim,
              background:T.bg, padding:"4px 10px", borderRadius:6
            }}>
              ${fmt(activePos.current_price, 4)}
            </div>
          </div>
          <PositionChart
            pos={activePos}
            projection={projections?.[activePos.id]}
          />
        </div>
      )}
    </div>
  );
}

// ─── Existing components (Login, Signals, Positions, Performance, Market) ─────

function Login({ onLogin }) {
  const [pw, setPw]   = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit() {
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${API}/api/login`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ password: pw })
      });
      if (r.ok) {
        const d = await r.json();
        localStorage.setItem("token", d.token);
        onLogin(d.token);
      } else {
        setErr("Invalid password");
      }
    } catch { setErr("Connection error"); }
    setLoading(false);
  }

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center",
                  justifyContent:"center", background:T.bg }}>
      <div style={{ width:360, padding:40, background:T.surface,
                    borderRadius:16, border:`1px solid ${T.border}` }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:28, marginBottom:8 }}>⚡</div>
          <div style={{ fontSize:20, fontWeight:600, color:T.accent }}>Crypto Agent</div>
          <div style={{ fontSize:12, color:T.textDim, marginTop:4 }}>Wyckoff AI Engine</div>
        </div>
        <input
          type="password" placeholder="Password"
          value={pw} onChange={e => setPw(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()}
          style={{ width:"100%", padding:"12px 16px", borderRadius:8,
                   border:`1px solid ${T.border}`, background:T.bg,
                   color:T.text, fontSize:14, outline:"none", marginBottom:12,
                   fontFamily:"inherit" }}
        />
        {err && <div style={{ color:T.red, fontSize:12, marginBottom:8 }}>{err}</div>}
        <button onClick={submit} disabled={loading}
          style={{ width:"100%", padding:"12px", borderRadius:8,
                   background:T.accent, color:T.bg, fontWeight:600,
                   fontSize:14, border:"none", cursor:"pointer",
                   opacity: loading ? 0.6 : 1, fontFamily:"inherit" }}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </div>
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background:T.surface, borderRadius:12,
                  border:`1px solid ${T.border}`, padding:20,
                  ...style }}>
      {children}
    </div>
  );
}

function Badge({ label, color }) {
  return (
    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:4,
                   background:`${color}22`, color, fontWeight:600,
                   textTransform:"uppercase", letterSpacing:"0.06em" }}>
      {label}
    </span>
  );
}

function SignalsTab({ signals, token, onAction }) {
  const pending = signals.filter(s => s.status === "pending");

  async function act(id, action) {
    await fetch(`${API}/api/${action}/${id}`, {
      method:"POST", headers:{ Authorization:`Bearer ${token}` }
    });
    onAction();
  }

  if (!pending.length) {
    return (
      <div style={{ textAlign:"center", padding:60, color:T.textDim }}>
        <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
        <div style={{ fontSize:14 }}>No pending signals</div>
        <div style={{ fontSize:12, marginTop:6 }}>Agent scanning every 90 minutes</div>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {pending.map(s => (
        <Card key={s.id}>
          <div style={{ display:"flex", justifyContent:"space-between",
                        alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <span style={{ fontSize:20, fontWeight:700, color:T.accent }}>{s.symbol}</span>
                <Badge label={s.direction} color={T.green} />
                {s.strategy === "FUNDING_SQUEEZE" &&
                  <Badge label="FUNDING SQUEEZE" color={T.purple} />}
              </div>
              <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:12 }}>
                {[
                  ["Price",      `$${fmt(s.price, 4)}`],
                  ["Score",      `${s.score}/12`],
                  ["Confidence", `${s.confidence}%`],
                  ["Monthly",    s.monthly_regime],
                  ["Weekly",     s.weekly],
                ].map(([k,v]) => (
                  <div key={k}>
                    <div style={{ fontSize:10, color:T.textDim, marginBottom:2 }}>{k}</div>
                    <div className="mono" style={{ fontSize:12, color:T.text }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:12, color:T.textDim, lineHeight:1.6,
                            maxWidth:500, marginBottom:8 }}>
                {s.reasoning}
              </div>
              {s.risk_factors && (
                <div style={{ fontSize:11, color:T.yellow, lineHeight:1.5 }}>
                  ⚠️ {s.risk_factors}
                </div>
              )}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <button onClick={() => act(s.id, "approve")}
                style={{ padding:"10px 24px", borderRadius:8, border:"none",
                         background:T.green, color:T.bg, fontWeight:700,
                         fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                ✓ Approve
              </button>
              <button onClick={() => act(s.id, "reject")}
                style={{ padding:"10px 24px", borderRadius:8, border:`1px solid ${T.red}`,
                         background:"transparent", color:T.red, fontWeight:600,
                         fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                ✕ Reject
              </button>
            </div>
          </div>
          {/* Position sizes */}
          {s.positions && Object.entries(s.positions).map(([pn, pos]) => (
            <div key={pn} style={{ marginTop:8, padding:"8px 12px",
                                   background:T.bg, borderRadius:8,
                                   display:"flex", gap:16, flexWrap:"wrap" }}>
              <Badge label={pn.toUpperCase()} color={T.accent} />
              {[
                ["Size",  `$${fmt(pos.position_usd, 0)}`],
                ["Stop",  `$${fmt(pos.stop_loss, 4)}`],
                ["TP1",   `$${fmt(pos.take_profit_1, 4)}`],
                ["TP2",   `$${fmt(pos.take_profit, 4)}`],
              ].map(([k,v]) => (
                <div key={k} className="mono" style={{ fontSize:11, color:T.textDim }}>
                  <span style={{ color:T.textMuted }}>{k}: </span>{v}
                </div>
              ))}
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

function PositionsTab({ positions, token, onAction }) {
  const open = positions.filter(p => p.status === "OPEN" || !p.status);

  async function closePos(id, type) {
    await fetch(`${API}/api/close/${id}`, {
      method:"POST",
      headers:{ Authorization:`Bearer ${token}`, "Content-Type":"application/json" },
      body: JSON.stringify({ type })
    });
    onAction();
  }

  if (!open.length) {
    return (
      <div style={{ textAlign:"center", padding:60, color:T.textDim }}>
        <div style={{ fontSize:32, marginBottom:12 }}>📭</div>
        <div style={{ fontSize:14 }}>No open positions</div>
      </div>
    );
  }

  const totalInvested = open.reduce((s,p) => s + (parseFloat(p.position_usd)||0), 0);
  const totalPnl      = open.reduce((s,p) => s + (parseFloat(p.pnl)||0), 0);
  const totalValue    = totalInvested + totalPnl;

  return (
    <div>
      {/* Summary row */}
      <div style={{ display:"flex", gap:16, marginBottom:20, flexWrap:"wrap" }}>
        {[
          { label:"Invested",      val:`$${fmt(totalInvested, 2)}`, color:T.text  },
          { label:"Current Value", val:`$${fmt(totalValue, 2)}`,    color:T.text  },
          { label:"Total P&L",     val:`$${fmt(totalPnl, 2)}`,      color:clr(totalPnl) },
          { label:"Positions",     val:open.length,                  color:T.accent },
        ].map(s => (
          <Card key={s.label} style={{ flex:"1 1 140px", padding:"14px 18px" }}>
            <div style={{ fontSize:10, color:T.textDim, textTransform:"uppercase",
                          letterSpacing:"0.08em", marginBottom:6 }}>{s.label}</div>
            <div className="mono" style={{ fontSize:18, fontWeight:600, color:s.color }}>
              {s.val}
            </div>
          </Card>
        ))}
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {open.map(p => {
          const pnl    = parseFloat(p.pnl || 0);
          const pnlPct = parseFloat(p.pnl_pct || 0);
          const entry  = parseFloat(p.entry_price);
          const curr   = parseFloat(p.current_price || entry);
          return (
            <Card key={p.id}>
              <div style={{ display:"flex", justifyContent:"space-between",
                            alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:10 }}>
                    <span style={{ fontSize:18, fontWeight:700, color:T.text }}>{p.symbol}</span>
                    <Badge label={p.profile?.toUpperCase() || "—"} color={T.accent} />
                    <Badge label={p.direction || "BUY"} color={T.green} />
                  </div>
                  <div style={{ display:"flex", gap:20, flexWrap:"wrap" }}>
                    {[
                      ["Entry",   `$${fmt(entry, 4)}`,      T.textDim],
                      ["Current", `$${fmt(curr, 4)}`,       clr(pnlPct)],
                      ["P&L $",   `$${fmt(pnl, 2)}`,        clr(pnl)],
                      ["P&L %",   pct(pnlPct),              clr(pnlPct)],
                      ["Stop",    `$${fmt(p.stop_loss, 4)}`, T.red],
                      ["TP",      `$${fmt(p.take_profit, 4)}`, T.green],
                    ].map(([k,v,c]) => (
                      <div key={k}>
                        <div style={{ fontSize:10, color:T.textDim, marginBottom:2 }}>{k}</div>
                        <div className="mono" style={{ fontSize:12, color:c }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => closePos(p.id, "half")}
                    style={{ padding:"8px 14px", borderRadius:8, border:`1px solid ${T.yellow}`,
                             background:"transparent", color:T.yellow, fontSize:12,
                             cursor:"pointer", fontFamily:"inherit" }}>
                    50% Exit
                  </button>
                  <button onClick={() => closePos(p.id, "full")}
                    style={{ padding:"8px 14px", borderRadius:8, border:`1px solid ${T.red}`,
                             background:"transparent", color:T.red, fontSize:12,
                             cursor:"pointer", fontFamily:"inherit" }}>
                    Full Exit
                  </button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function PerformanceTab({ performance }) {
  const p = performance || {};
  return (
    <div>
      <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:20 }}>
        {[
          { label:"Total Trades", val:p.total_trades ?? "—",          color:T.text  },
          { label:"Win Rate",     val:`${p.win_rate ?? 0}%`,          color:p.win_rate > 40 ? T.green : T.yellow },
          { label:"Wins",         val:p.wins ?? 0,                    color:T.green },
          { label:"Losses",       val:p.losses ?? 0,                  color:T.red   },
          { label:"Total P&L",    val:`$${fmt(p.total_pnl ?? 0, 2)}`, color:clr(p.total_pnl) },
        ].map(s => (
          <Card key={s.label} style={{ flex:"1 1 140px", padding:"14px 18px" }}>
            <div style={{ fontSize:10, color:T.textDim, textTransform:"uppercase",
                          letterSpacing:"0.08em", marginBottom:6 }}>{s.label}</div>
            <div className="mono" style={{ fontSize:20, fontWeight:600, color:s.color }}>
              {s.val}
            </div>
          </Card>
        ))}
      </div>
      <Card>
        <div style={{ fontSize:12, color:T.textDim, lineHeight:2 }}>
          <div>Backtest baseline win rate: <span className="mono" style={{ color:T.text }}>36.9%</span></div>
          <div>V4.2 backtest win rate: <span className="mono" style={{ color:T.text }}>40.8%</span></div>
          <div>V5 full-system backtest: <span className="mono" style={{ color:T.text }}>41.8%</span></div>
          <div>High macro confluence win rate: <span className="mono" style={{ color:T.green }}>50.0%</span></div>
        </div>
      </Card>
    </div>
  );
}

function MarketTab({ market }) {
  const m = market || {};
  const fg = m.fear_greed;
  const fgColor = fg >= 75 ? T.green : fg <= 25 ? T.red : T.yellow;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
        {[
          { label:"Fear & Greed",    val:fg ? `${fg} — ${m.fear_greed_label}` : "—", color:fgColor },
          { label:"Trend",           val:m.fear_greed_trend ?? "—",     color:T.text   },
          { label:"Market Regime",   val:m.market_regime ?? "—",        color:T.accent },
          { label:"Flash Crash",     val:m.flash_crash ?? "—",          color:m.flash_crash === "GREEN" ? T.green : T.red },
          { label:"Learning",        val:m.learning_confidence ?? "—",  color:T.textDim },
        ].map(s => (
          <Card key={s.label} style={{ flex:"1 1 160px", padding:"14px 18px" }}>
            <div style={{ fontSize:10, color:T.textDim, textTransform:"uppercase",
                          letterSpacing:"0.08em", marginBottom:6 }}>{s.label}</div>
            <div className="mono" style={{ fontSize:14, fontWeight:500, color:s.color }}>
              {s.val}
            </div>
          </Card>
        ))}
      </div>
      <Card>
        <div style={{ fontSize:12, color:T.textDim }}>
          Last scan: <span className="mono" style={{ color:T.text }}>{m.last_scan ?? "—"}</span>
        </div>
        {m.price_update && (
          <div style={{ fontSize:12, color:T.textDim, marginTop:4 }}>
            Last P&L update: <span className="mono" style={{ color:T.accent }}>{m.price_update}</span>
            <span style={{ marginLeft:6, fontSize:10, color:T.green }}>● live (5-min)</span>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── App shell ────────────────────────────────────────────────────────────────
export default function App() {
  const [token,       setToken]       = useState(localStorage.getItem("token"));
  const [tab,         setTab]         = useState("signals");
  const [signals,     setSignals]     = useState([]);
  const [positions,   setPositions]   = useState([]);
  const [performance, setPerformance] = useState({});
  const [market,      setMarket]      = useState({});
  const [projections, setProjections] = useState({});
  const [lastUpdate,  setLastUpdate]  = useState(null);
  const [scanning,    setScanning]    = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    try {
      const [sigs, pos, perf, mkt] = await Promise.all([
        fetch(`${API}/api/signals`,     { headers:h }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/api/positions`,   { headers:h }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/api/performance`, { headers:h }).then(r => r.ok ? r.json() : {}),
        fetch(`${API}/api/market`,      { headers:h }).then(r => r.ok ? r.json() : {}),
      ]);
      setSignals(sigs || []);
      setPositions(pos || []);
      setPerformance(perf || {});
      setMarket(mkt || {});
      setLastUpdate(new Date());
    } catch (e) {
      console.error("Load error:", e);
      if (e.message?.includes("401")) {
        localStorage.removeItem("token");
        setToken(null);
      }
    }
  }, [token]);

  // Load projections separately
  const loadProjections = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/projections`, {
        headers:{ Authorization:`Bearer ${token}` }
      });
      if (r.ok) {
        const data = await r.json();
        setProjections(data || {});
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    load();
    loadProjections();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [load, loadProjections]);

  async function triggerScan() {
    setScanning(true);
    await fetch(`${API}/api/scan`, {
      method:"POST",
      headers:{ Authorization:`Bearer ${token}`, "Content-Type":"application/json" }
    });
    setTimeout(() => { setScanning(false); load(); }, 3000);
  }

  async function emergencyClose() {
    if (!window.confirm("⚠️ Close ALL positions immediately?")) return;
    await fetch(`${API}/api/emergency`, {
      method:"POST",
      headers:{ Authorization:`Bearer ${token}`, "Content-Type":"application/json" }
    });
    load();
  }

  if (!token) return <Login onLogin={t => { setToken(t); localStorage.setItem("token", t); }} />;

  const pendingCount = signals.filter(s => s.status === "pending").length;
  const openCount    = positions.filter(p => p.status === "OPEN" || !p.status).length;

  const TABS = [
    { id:"signals",     label:"Signals",     badge:pendingCount },
    { id:"positions",   label:"Positions",   badge:openCount    },
    { id:"charts",      label:"Charts",      badge:null         },
    { id:"performance", label:"Performance", badge:null         },
    { id:"market",      label:"Market",      badge:null         },
  ];

  return (
    <div style={{ minHeight:"100vh", background:T.bg }}>
      <style>{css}</style>

      {/* Header */}
      <div style={{ background:T.surface, borderBottom:`1px solid ${T.border}`,
                    padding:"0 24px", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex",
                      alignItems:"center", justifyContent:"space-between",
                      height:56 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <span style={{ fontSize:16, fontWeight:700, color:T.accent }}>⚡ Crypto Agent</span>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div className="live-dot" />
              <span style={{ fontSize:10, color:T.textDim }}>
                {lastUpdate ? lastUpdate.toLocaleTimeString() : "connecting..."}
              </span>
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={triggerScan} disabled={scanning}
              style={{ padding:"6px 14px", borderRadius:8,
                       border:`1px solid ${T.accent}`, background:"transparent",
                       color:T.accent, fontSize:12, cursor:"pointer",
                       opacity:scanning ? 0.5 : 1, fontFamily:"inherit" }}>
              {scanning ? "Scanning..." : "Scan Now"}
            </button>
            <button onClick={emergencyClose}
              style={{ padding:"6px 14px", borderRadius:8,
                       border:`1px solid ${T.red}`, background:"transparent",
                       color:T.red, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
              Emergency Close
            </button>
            <button onClick={() => { localStorage.removeItem("token"); setToken(null); }}
              style={{ padding:"6px 14px", borderRadius:8,
                       border:`1px solid ${T.border}`, background:"transparent",
                       color:T.textDim, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
              Logout
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", gap:4 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding:"10px 16px", border:"none", background:"transparent",
                       color: tab === t.id ? T.accent : T.textDim,
                       fontSize:13, fontWeight: tab === t.id ? 600 : 400,
                       cursor:"pointer", borderBottom: tab === t.id ? `2px solid ${T.accent}` : "2px solid transparent",
                       transition:"all 0.2s", position:"relative", fontFamily:"inherit" }}>
              {t.label}
              {t.badge > 0 && (
                <span style={{ marginLeft:6, background:T.accent, color:T.bg,
                               borderRadius:10, fontSize:10, padding:"1px 6px",
                               fontWeight:700 }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth:1100, margin:"0 auto", padding:"24px 24px" }}>
        <div style={{ animation:"fadeIn 0.3s ease" }}>
          {tab === "signals"     && <SignalsTab     signals={signals}         token={token} onAction={load} />}
          {tab === "positions"   && <PositionsTab   positions={positions}     token={token} onAction={load} />}
          {tab === "charts"      && <ChartsTab      positions={positions}     projections={projections} />}
          {tab === "performance" && <PerformanceTab performance={performance} />}
          {tab === "market"      && <MarketTab      market={market} />}
        </div>
      </div>
    </div>
  );
}
